import cn from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    Divider,
    type FileWithUrl,
    FlexContainer,
    MenuText,
    Modal,
    TextAreaField,
    UtilityAlertIcon,
} from '@placer-ui/components';
import {
    selectOfflineReportsRequestsComments,
    selectOfflineReportsRequestsPostingMailMsg,
    selectSupportSelectedRequest,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { SupportRequestComment } from 'features/offline-reports/support-requests/components/support-requests-modals/components/support-request-comment/support-request-comment';
import { TitleModalHeader } from 'features/offline-reports/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { SupportRequestsFileUpload } from 'features/offline-reports/support-requests/components/support-requests-modals/components/support-requests-file-upload/support-requests-file-upload';
import styles from './support-requests-message-modal.module.scss';
import { supportedFileTypes } from 'features/offline-reports/support-requests/components/support-requests-modals/consts/consts';
import { useOpenSupportRequest } from 'features/offline-reports/support-requests/components/support-requests-modals/support-modal-manager';

export const SupportRequestsMessageModal = () => {
    const [filesWithUrl, setFilesWithUrl] = useState<FileWithUrl[]>([]);
    const comments = useSelector(selectOfflineReportsRequestsComments);
    const selectedRequest = useSelector(selectSupportSelectedRequest);
    const [comment, setComment] = useState('');
    const { postCaseEmailMsg } = useOfflineReportsActions();
    const { openSupportRequest } = useOpenSupportRequest();
    const isPostingMsg = useSelector(selectOfflineReportsRequestsPostingMailMsg);

    const isClosedRequest = selectedRequest?.status === 'Closed';
    const sendMessage = async () => {
        const { id, subject } = selectedRequest || {};
        if (!id) return;

        const response = await postCaseEmailMsg({
            caseId: String(id),
            comment,
            files: filesWithUrl.map(({ file }) => file),
            requestSubject: isClosedRequest ? `Re: ${id} - ${subject}` : undefined,
        });

        if (response.meta.requestStatus === 'rejected') {
            openSupportRequest({ flowType: 'failed', retryType: 'message' });
        } else {
            openSupportRequest({ flowType: 'info', props: { requestId: id } });
        }
    };

    if (!selectedRequest) return null;

    const { subject, id: requestId } = selectedRequest;

    return (
        <>
            <TitleModalHeader title={subject} />
            <Modal.Content>
                <div className={styles.modalBody}>
                    {comments.length > 0 && (
                        <>
                            <div className={cn(styles.comments, styles.fixedHeight)}>
                                <SupportRequestComment comment={comments[comments.length - 1]} />
                            </div>
                            <Divider />
                        </>
                    )}
                    <TextAreaField
                        value={comment}
                        onChange={setComment}
                        label="Reply a message on this thread"
                        placeholder="Write your message here"
                        initialHeight={104}
                    />
                    <SupportRequestsFileUpload
                        files={filesWithUrl}
                        setFiles={setFilesWithUrl}
                        formats={supportedFileTypes}
                        feedback={[]}
                    />
                </div>
            </Modal.Content>
            <Modal.Footer>
                <Modal.Footer.RightControls>
                    <Button
                        testId="support-request-message-modal-cancel-button"
                        variant="secondary-blue"
                        fullWidth
                        onClick={() =>
                            openSupportRequest({ flowType: 'info', props: { requestId } })
                        }
                        disabled={isPostingMsg}
                    >
                        Cancel
                    </Button>
                    <Button
                        testId="support-request-message-modal-submit-button"
                        variant="primary-brand"
                        fullWidth
                        onClick={sendMessage}
                        disabled={isPostingMsg || !comment}
                        isLoading={isPostingMsg}
                    >
                        {!isPostingMsg && 'Send a Message'}
                    </Button>
                </Modal.Footer.RightControls>
                {isClosedRequest && (
                    <Modal.Footer.LeftControls>
                        <FlexContainer gap={8} alignItems="center">
                            <UtilityAlertIcon />
                            <MenuText
                                variant="semi-bold"
                                color="secondary"
                                testId="support-request-modal-closed-message"
                            >
                                Ticket closed. New replies will open a separate ticket.
                            </MenuText>
                        </FlexContainer>
                    </Modal.Footer.LeftControls>
                )}
            </Modal.Footer>
        </>
    );
};
